<template>
    <div class="work-location-card-mobile">
        <div class="header">
            <div class="header-text">
                <h3 class="title">{{ $t('landing-page.work-location') }}</h3>
                <p class="subtitle">{{ $t('landing-page.work-location-description') }}</p>
            </div>
            <div class="button-wrapper">
                <AnimatedButton />
            </div>
        </div>

        <div class="tabs">
            <div class="tab" :class="{ active: workLocation === 'inOffice' }" @click="workLocation = 'inOffice'">
                <v-icon>mdi-briefcase</v-icon>
                <span>{{ $t('landing-page.in-office') }}</span>
            </div>
            <div class="tab" :class="{ active: workLocation === 'remoteWork' }" @click="workLocation = 'remoteWork'">
                <v-icon>mdi-home</v-icon>
                <span>{{ $t('landing-page.remote-work') }}</span>
            </div>
        </div>

        <div class="content">
            <div v-if="workLocation === 'inOffice'" class="in-office-grid">
                <div v-for="(card, index) in inOfficeCards" :key="index" class="info-card" :class="card.background">
                    <div class="info-text">
                        <p class="info-title">{{ card.title }}</p>
                        <p class="info-desc">{{ card.desc }}</p>
                    </div>
                    <img :src="card.image" class="info-image" />
                </div>
            </div>

            <div v-else class="remote-work">
                <p class="section-header">{{ $t('landing-page.select-hours') }}</p>

                <div class="today-date">
                    <span class="label">{{ $t('booking.today') }}</span>
                    <span class="date">{{ moment().format('DD/MM/YYYY') }}</span>
                </div>

                <v-row dense class="selects-grid">
                    <v-col cols="12" sm="6">
                        <v-select v-model="startTime" :items="times" :label="$t('landing-page.work-start')"
                            :disabled="isSaveDisabled" variant="outlined" density="comfortable" rounded />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-select v-model="endTime" :items="times" :label="$t('landing-page.return-home')"
                            :disabled="isSaveDisabled" variant="outlined" density="comfortable" rounded />
                    </v-col>
                    <v-col cols="12">
                        <v-select v-model="lunchDuration" :items="lunchDurations" :disabled="isSaveDisabled"
                            :label="$t('landing-page.lunch-break')" variant="outlined" density="comfortable" rounded />
                    </v-col>
                </v-row>

                <div class="save-button-container">
                    <v-btn color="primary" rounded class="save-button" :disabled="isSaveDisabled" @click="saveSchedule">
                        {{ $t('landing-page.save') }}
                    </v-btn>
                    <p v-if="isSaveDisabled" class="save-hint">{{ $t('landing-page.contact-admin') }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import moment from 'moment'
import { useI18n } from 'vue-i18n'
import AnimatedButton from '@/components/general/ChildComponents/AnimatedButton.vue'
import snacks from '@/assets/Branding/Pia/snacks.png'
import lunch from '@/assets/Branding/Pia/lunch.png'
import meeting from '@/assets/Branding/Pia/meeting.png'
import fun from '@/assets/Branding/Pia/fun.png'
import { isWebPlatform, getStoredItem, setStoredItem, removeStoredItem } from '@/services/utils.js'

const workLocation = ref('inOffice')
const startTime = ref('09:00')
const endTime = ref('18:00')
const lunchDuration = ref('30 min')
const isSaveDisabled = ref(false)
const currentDate = ref(moment().format('DD/MM/YYYY'))
const { locale, t } = useI18n()

const times = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00']
const lunchDurations = ['30 min', '45 min', '1 hour']

const inOfficeCards = computed(() => [
    {
        title: t('landing-page.enjoy-snacks'),
        desc: t('landing-page.snacks-description'),
        image: snacks,
        background: 'card-blue'
    },
    {
        title: t('landing-page.enjoy-lunch'),
        desc: t('landing-page.lunch-description'),
        image: lunch,
        background: 'card-yellow'
    },
    {
        title: t('landing-page.enjoy-break'),
        desc: t('landing-page.break-description'),
        image: meeting,
        background: 'card-lightblue'
    },
    {
        title: t('landing-page.enjoy-events'),
        desc: t('landing-page.events-description'),
        image: fun,
        background: 'card-purple'
    }
])

// Load saved schedule and check if save should be disabled
onMounted(async () => {
    const savedSchedule = await getStoredItem('remoteWorkSchedule')
    if (savedSchedule && savedSchedule.date === currentDate.value) {
        startTime.value = savedSchedule.startTime
        endTime.value = savedSchedule.endTime
        lunchDuration.value = savedSchedule.lunchDuration
        isSaveDisabled.value = true
    }
})

// Watch for date changes
watch(() => moment().format('DD/MM/YYYY'), async (newDate) => {
    if (newDate !== currentDate.value) {
        currentDate.value = newDate
        isSaveDisabled.value = false
        await removeStoredItem('remoteWorkSchedule')
    }
})

// Save schedule
const saveSchedule = async () => {
    const schedule = {
        date: currentDate.value,
        startTime: startTime.value,
        endTime: endTime.value,
        lunchDuration: lunchDuration.value
    }
    await setStoredItem('remoteWorkSchedule', schedule)
    isSaveDisabled.value = true
}
</script>

<style scoped>
.work-location-card-mobile {
    background: white;
    border-radius: 14px;
    padding: 20px;
    margin-top: 20px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
}

.header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.header-text {
    text-align: center;
}

.button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
}

.title {
    font-weight: bold;
    font-size: 1.4rem;
}

.subtitle {
    font-size: 0.9rem;
    color: #6b7280;
}

.tabs {
    display: flex;
    margin-top: 20px;
    border-bottom: 1px solid #e0e0e0;
    justify-content: space-evenly;
    gap: 10px;
}

.tab {
    flex: 1;
    text-align: center;
    padding: 10px;
    cursor: pointer;
    color: #374151;
    font-weight: 500;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
}

.tab.active {
    color: #2563eb;
    font-weight: bold;
    border-bottom: 2px solid #2563eb;
}

.content {
    margin-top: 20px;
}

/* In-Office */
.in-office-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
}

.info-card {
    background: #f3f4f6;
    border-radius: 12px;
    padding: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 100px;
}

.info-text {
    flex: 1;
    margin-right: 8px;
}

.info-title {
    font-weight: bold;
    font-size: 14px;
    margin-bottom: 4px;
}

.info-desc {
    font-size: 12px;
    color: #6b7280;
}

.info-image {
    width: 80px;
    height: 60px;
    object-fit: cover;
    clip-path: polygon(40% 0%, 100% 0%, 100% 100%, 0 100%);
}

/* Remote Work */
.remote-work {
    margin-top: 10px;
}

.section-header {
    font-weight: bold;
    font-size: 16px;
    margin-bottom: 10px;
}

.today-date {
    display: flex;
    justify-content: space-between;
    background: #f3f4f6;
    padding: 8px 14px;
    border-radius: 8px;
    font-weight: bold;
    margin-bottom: 10px;
}

.label {
    color: #374151;
}

.date {
    color: #2563eb;
}

.selects-grid {
    margin-top: 10px;
}

.save-button-container {
    align-self: flex-end;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-top: 10px;
}

.save-button {
    width: 100px;
}

.save-hint {
    font-size: 12px;
    color: #6b7280;
    margin-top: 5px;
    text-align: right;
}

/* Colors */
.card-blue {
    background: #eff6ff;
}

.card-yellow {
    background: #fef9c3;
}

.card-lightblue {
    background: #e0f2fe;
}

.card-purple {
    background: #f3e8ff;
}

/* 📱 Responsive Fixes */
@media (max-width: 770px) {
    .info-title {
        font-size: 13px;
    }

    .info-desc {
        font-size: 11px;
    }

    .info-image {
        width: 70px;
        height: 50px;
    }
}

@media (max-width: 600px) {
    .tabs {
        gap: 8px;
    }

    .tab {
        padding: 8px;
        font-size: 14px;
    }
}

@media (max-width: 480px) {
    .in-office-grid {
        grid-template-columns: 1fr;
    }

    .info-card {
        padding: 10px;
    }

    .info-title {
        font-size: 13px;
    }

    .info-desc {
        font-size: 10px;
    }

    .info-image {
        width: 60px;
        height: 40px;
    }
}

@media (max-width: 375px) {
    .info-title {
        font-size: 12px;
    }

    .info-desc {
        font-size: 9.5px;
    }

    .info-card {
        padding: 8px;
    }
}
</style>